import { Tag } from 'antd';
import classNames from 'classnames';
import isNil from 'lodash/isNil';
import React, { PropsWithChildren } from 'react';

import LearnMoreTooltip from 'components/atoms/LearnMoreTooltip';
import {
  DELAY_HELP_ARTICLE_LINK_FARMBOT,
  DELAY_HELP_ARTICLE_LINK_RANCHBOT,
  HIBERNATION_HELP_ARTICLE_LINK_FARMBOT,
  HIBERNATION_HELP_ARTICLE_LINK_RANCHBOT
} from 'constants/links';
import { DeviceStatus } from 'types/device.enum';
import { NonProblematicSampleStatusCode, SampleStatusCode } from 'types/sample.enum';
import { isRanchbot } from 'utils/is-x-bot';

import { prioritiseStatus } from './prioritise-status';

import './styles.less';

// ==============================
// MAPPING RULES
// ==============================
const DEVICE_STATUS_TO_TAG_TEXT: Record<DeviceStatus, string> = {
  [DeviceStatus.ASSIGNED_TO_ENTERPRISE]: 'ASSIGNED',
  [DeviceStatus.DECOMMISSIONED]: 'DECOMMISSIONED',
  [DeviceStatus.DELAYED]: 'DELAY',
  [DeviceStatus.DISCONNECTED]: 'DISCONNECTED',
  [DeviceStatus.HIBERNATING]: 'HIBERNATION',
  [DeviceStatus.ONLINE]: 'ONLINE',
  [DeviceStatus.TURNED_OFF]: 'MUTED',
  [DeviceStatus.UNASSIGNED_FROM_ENTERPRISE]: 'UNASSIGNED',
  [DeviceStatus.UNKNOWN]: 'UNKNOWN'
};

const DEVICE_STATUS_TO_TAG_COLOUR: Record<DeviceStatus, undefined | 'grey' | 'red'> = {
  [DeviceStatus.ASSIGNED_TO_ENTERPRISE]: undefined,
  [DeviceStatus.DECOMMISSIONED]: undefined,
  [DeviceStatus.DELAYED]: 'red',
  [DeviceStatus.DISCONNECTED]: 'grey',
  [DeviceStatus.HIBERNATING]: 'grey',
  [DeviceStatus.ONLINE]: undefined,
  [DeviceStatus.TURNED_OFF]: 'grey',
  [DeviceStatus.UNASSIGNED_FROM_ENTERPRISE]: 'grey',
  [DeviceStatus.UNKNOWN]: 'grey'
};

const SAMPLE_STATUS_CODE_TO_TAG_TEXT: Record<Exclude<SampleStatusCode, NonProblematicSampleStatusCode>, string> = {
  [SampleStatusCode.NO_TX_RECEIVED]: 'NO DATA',
  [SampleStatusCode.BAD_SENSOR]: 'ERROR',
  [SampleStatusCode.BAD_SENSOR_H]: 'ERROR',
  [SampleStatusCode.BAD_SENSOR_L]: 'ERROR'
};

const SAMPLE_STATUS_CODE_TO_TAG_COLOUR: Record<Exclude<SampleStatusCode, NonProblematicSampleStatusCode>, 'red' | 'yellow'> = {
  [SampleStatusCode.NO_TX_RECEIVED]: 'red',
  [SampleStatusCode.BAD_SENSOR]: 'yellow',
  [SampleStatusCode.BAD_SENSOR_H]: 'yellow',
  [SampleStatusCode.BAD_SENSOR_L]: 'yellow'
};

// ==============================
// TYPES
// ==============================
interface StatusTagProps {
  deviceStatus: DeviceStatus;
  latestSampleStatus?: SampleStatusCode;
  /** Default to false */
  showTooltip?: boolean;
  /** Default to false */
  showOnlineStatus?: boolean;
  /** Useful for compact containers, e.g. mobile header */
  asPlainText?: boolean;
  className?: string;
}

interface TagViewProps extends Pick<StatusTagProps, 'asPlainText' | 'className'> {
  text: string,
  variant?: string,
}

// ==============================
// COMPONENTS
// ==============================
const TagView = ({ text, variant, asPlainText, className }: TagViewProps) => asPlainText
  ? <div className={className}>{text}</div>
  : <Tag className={classNames('StatusTag', variant, className)}>{text}</Tag>;

const DelayTagTooltip = ({ children }: PropsWithChildren) => (
  <LearnMoreTooltip
    title="When a monitor is in DELAY status, it means we have not heard from
it in some time and it may need to be checked."
    link={
      isRanchbot()
        ? DELAY_HELP_ARTICLE_LINK_RANCHBOT
        : DELAY_HELP_ARTICLE_LINK_FARMBOT
    }
    showInfoIcon={false}
  >
    {children}
  </LearnMoreTooltip>
);

const HibernatingTagTooltip = ({
  children
}: PropsWithChildren) => (
  <LearnMoreTooltip
    title="When a monitor is in Hibernation status, it means it is in a power-saving mode and will not transmit."
    link={
      isRanchbot()
        ? HIBERNATION_HELP_ARTICLE_LINK_RANCHBOT
        : HIBERNATION_HELP_ARTICLE_LINK_FARMBOT
    }
    showInfoIcon={false}
  >
    {children}
  </LearnMoreTooltip>
);

const WithTooltip = ({ status, children }: PropsWithChildren<{ status: DeviceStatus | SampleStatusCode }>) => {
  if (status === DeviceStatus.DELAYED) {
    return <DelayTagTooltip>{children}</DelayTagTooltip>;
  }
  if (status === DeviceStatus.HIBERNATING) {
    return <HibernatingTagTooltip>{children}</HibernatingTagTooltip>;
  }
  // The Tooltip wrapper seems to add a 'span' around our tagView. Hence, we mimic that structure
  // to ensure alignment among tags with prop `showTooltip=true`.
  return <span>{children}</span>;
};

/** Renders device or latest sample status with prioritisation. */
const StatusTag = ({
  deviceStatus,
  latestSampleStatus,
  showTooltip = false,
  showOnlineStatus = false,
  asPlainText,
  className
}: StatusTagProps) => {
  const status: DeviceStatus | SampleStatusCode = !isNil(latestSampleStatus)
    ? prioritiseStatus(deviceStatus, latestSampleStatus)
    : deviceStatus;
  // ============================== HANDLE SAMPLE STATUS CODE ==============================
  if (typeof status === 'number') {
    const text = SAMPLE_STATUS_CODE_TO_TAG_TEXT[status];
    const colour = SAMPLE_STATUS_CODE_TO_TAG_COLOUR[status];
    const tagView = text
      ? <TagView text={text} variant={colour} asPlainText={asPlainText} className={className} />
      : null; // non-problematic or unknown
    return showTooltip
      ? <WithTooltip status={status}>{tagView}</WithTooltip>
      : tagView;
  }

  // ============================== HANDLE DEVICE STATUS ==============================
  if (status === DeviceStatus.ONLINE && !showOnlineStatus) {
    return null;
  }

  const text = DEVICE_STATUS_TO_TAG_TEXT[status];
  const colour = DEVICE_STATUS_TO_TAG_COLOUR[status];
  const tagView = text
    ? <TagView text={text} variant={colour} asPlainText={asPlainText} className={className} />
    : <TagView text="UNKNOWN" variant="grey" asPlainText={asPlainText} className={className} />;

  return showTooltip
    ? <WithTooltip status={status}>{tagView}</WithTooltip>
    : tagView;
};

export default StatusTag;
